import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { UrlTelemetryService } from '~commonInfra/url/UrlTelemetryService';
import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { showDMAwareness } from '~src/commonInfra/components/DMAwareness/DmAwarenessUtils';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { selectPreviousUrl } from '~src/spacesPlatform/selectors/NavigationStateSelectors';
import { DatasourceSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { RouterError } from '../../bootstrap/routerHelpers/RouterError';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { DatasourceSpaceProvider, DatasourceSpaceProviderProps } from './DatasourceSpaceProvider';

export interface SpaceRouteParams {
  readonly id: string;
}

export interface DatasourceSpaceRouteComponentProps extends RouteComponentProps<SpaceRouteParams> {}

export type DatasourceProviderFnReturnType = (props: DatasourceSpaceRouteComponentProps) => JSX.Element;
export type DatasourceProviderFn = (subspaceName: DatasourceSubspaceName, providerProps: DatasourceSpaceComponentWithRouterProps) => DatasourceProviderFnReturnType;

export interface DatasourceSpaceRouterOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly provider: DatasourceProviderFn;
}

export interface DatasourceSpaceRouterStateProps {
  readonly catalogingOnSiteEnabled: boolean;
  readonly isNLPServiceEnabled: boolean;
  readonly previousUrl: string;
  readonly isWebExtractionEnabled: boolean;
  readonly showDMAwareness: boolean;
}

export type DatasourceSpaceRouterProps = DatasourceSpaceRouterOwnProps & DatasourceSpaceRouterStateProps;

export class DatasourceSpaceRouterComponent extends React.Component<DatasourceSpaceRouterProps> {

  public render() {
    return (
      <Switch>
        <Route exact path={this.props.urls.datasourceAskDataPath(':id')} render={({ match }) => (
          // There might be code that still redirects to Ask Data path.
          // We don't want to break anything at a late stage of the release cycle.
          // We might remove this route in the beginning of the next releasce cycle,
          // So that teams have time to report and fix problems if they arise.
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />);
        <Route exact path={this.props.urls.datasourceConnectionsPath(':id')} render={this.props.provider('connections', this.props)} />
        <Route exact path={this.props.urls.datasourceExtractRefreshesPath(':id')} render={this.props.provider('extractRefreshes', this.props)} />
        <Route exact path={this.props.urls.datasourceLocalExtractRefreshesPath(':id')} render={this.props.provider('remoteRefreshSchedules', this.props)} />
        <Route exact path={this.props.urls.datasourceConnectedWorkbooksPath(':id')} render={this.props.provider('connectedWorkbooks', this.props)} />
        {this.props.showDMAwareness? this.getDmAwarenessLineageRoute(): this.getLineageRoute()}

        <Route exact path={this.props.urls.datasourcePublishresultPath(':id')} render={({ match }) => (
          // this has to be datasourceConnectionsPath and not datasourcePath so that displayPublishResult is
          // populated correctly because it checks the publishresult url from the redirect. If we insert
          // another redirect between /publishresult and rendering the provider, the previous url
          // is wrong.
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />
        <Route exact path={this.props.urls.datasourceDetailsPath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />
        <Route path={this.props.urls.datasourceGrantAccessPath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />

        <Route exact path={this.props.urls.datasourceDataConnectionPath(':id')} render={({ match }) => (
          // The Integrated Datasources feature removed the "data connection" page for datasources and
          // replaced it with the "data connections" page. If any old links point to the old page, this
          // redirect will get them to the right place.
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />

        <Route exact path={this.props.urls.datasourcePath(':id')} render={({ match }) => {
          // Query parameters are removed by redirect, so telemetry which relies
          // on them must be sent before.
          UrlTelemetryService.maybeSendDataQualitySubscriptionEvent(match.params.id, 'datasource');
          return <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />;
        }} />

        <Route render={this.error} />
      </Switch>
    );
  }

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  };

  private getDmAwarenessLineageRoute =  (): JSX.Element => {
      return <Route path={this.props.urls.datasourceLineagePath(':id')} render={this.props.provider('lineage', this.props)} />;
  };

  private getLineageRoute = (): JSX.Element => {
    if (this.props.catalogingOnSiteEnabled) {
      return <Route path={this.props.urls.datasourceLineagePath(':id')} render={this.props.provider('lineage', this.props)} />;
    } else {
      // we want to match on any data source path starting with /lineage so don't use 'exact' on the route
      return (
        <Route path={this.props.urls.datasourceLineagePath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.datasourceConnectionsPath(match.params.id)} />
        )} />);
    }
  };
}

export type DatasourceSpaceComponentWithRouterProps = Omit<DatasourceSpaceRouterProps, 'provider'>;
const provider: DatasourceProviderFn = (subspaceName: DatasourceSubspaceName, providerProps: DatasourceSpaceComponentWithRouterProps): DatasourceProviderFnReturnType => {
  return (props: DatasourceSpaceRouteComponentProps): JSX.Element => {
    const datasourceId = props.match.params.id;
    const displayPublishResult = providerProps.previousUrl.includes(providerProps.urls.datasourcePublishresultPath(datasourceId));
    const grantAccessDisplay = providerProps.previousUrl.includes(providerProps.urls.datasourceGrantAccessPath(datasourceId));

    const datasourceSpaceProviderProps: DatasourceSpaceProviderProps = {
      siteName: providerProps.siteName,
      subspaceName,
      routeLocationSearch: props.location.search,
      routeLocationPath: props.location.pathname,
      datasourceId,
      isNLPServiceEnabled: providerProps.isNLPServiceEnabled,
      urls: providerProps.urls,
      displayPublishResult,
      publishResultUrl: displayPublishResult ? providerProps.previousUrl : '',
      grantAccessDisplay: grantAccessDisplay,
      grantAccessUrl: grantAccessDisplay ? providerProps.previousUrl : '',
      isWebExtractionEnabled: providerProps.isWebExtractionEnabled,
    };

    return <DatasourceSpaceProvider {...datasourceSpaceProviderProps} />;
  };
};

type DatasourceSpaceComponentWithRouterOwnProps = Omit<DatasourceSpaceRouterOwnProps, 'urls' | 'provider'>;
const DatasourceSpaceComponentWithRouter = (props: DatasourceSpaceComponentWithRouterProps) => {
  return (
    <DatasourceSpaceRouterComponent {...props} provider={provider}/>
  );
};

const mapStateToProps: MapStateToProps<DatasourceSpaceRouterStateProps> = (state: AppState) => ({
  previousUrl: selectPreviousUrl(state),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state),
  isNLPServiceEnabled: SessionSiteSelectors.isNLPServiceEnabled(state),
  isWebExtractionEnabled: CompositeSessionInfoSelectors.isWebExtractionEnabled(state),
  showDMAwareness: showDMAwareness()
});

// Component must *not* be pure as to allow route updates to flow through
export const DatasourceSpaceRouter: React.ComponentClass<DatasourceSpaceComponentWithRouterOwnProps>
  = connect(mapStateToProps, null, null, {pure: false})(DatasourceSpaceComponentWithRouter);
